import React, { useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@roger/ui-components';
import { AlertTriangle, Loader2 } from 'lucide-react';

import { submitPortOrderFaxNumberPortsPortOrderIdSubmitPost as submitPortOrder } from '../../../services/api/sdk.gen';
import type { PortOrderDetail } from '../../../services/api/types.gen';
import {
  getBusinessErrorMessage,
  getSurfaceableError,
} from '../../../utils/apiErrorDetail';
import { getHttpStatus } from '../../../utils/httpErrorUtils';
import { displayPhone, logFailure, type Notify, toE164 } from './shared';

const SUBMIT_OUTCOME_UNKNOWN = 'submit_outcome_unknown';
const SUBMIT_UNKNOWN_MESSAGE =
  'Submission state unknown. Roger support will check it; do not try again until they do.';

/**
 * Only the server's unknown-outcome code, no answer at all, or an unflagged
 * 5xx can follow a confirm that went out. Any other answer means nothing was
 * sent, and saying otherwise would send the agency to support for nothing.
 */
const describeSubmitError = (error: unknown): string => {
  const surfaced = getSurfaceableError(error);
  if (surfaced) {
    return surfaced.errorCode === SUBMIT_OUTCOME_UNKNOWN
      ? SUBMIT_UNKNOWN_MESSAGE
      : surfaced.message;
  }
  const status = getHttpStatus(error);
  if (status !== undefined && status < 500) {
    return (
      getBusinessErrorMessage(error) ?? 'The port was not submitted. Try again.'
    );
  }
  return SUBMIT_UNKNOWN_MESSAGE;
};

/** Which file goes to the carrier as the invoice, as the server will send it. */
const invoiceSummary = (detail: PortOrderDetail): string => {
  const { loa, invoice } = detail.documents;
  if (invoice.attached && invoice.source !== 'loa') {
    return invoice.filename ?? 'Your invoice';
  }
  return `${loa.filename ?? 'Your signed letter'}, in place of an invoice`;
};

const SubmitDialog: React.FC<{
  detail: PortOrderDetail;
  onClose: () => void;
  onSubmitted: (detail: PortOrderDetail) => void;
  onFailed: () => void;
  showSuccess: Notify;
  showError: Notify;
}> = ({ detail, onClose, onSubmitted, onFailed, showSuccess, showError }) => {
  const [typed, setTyped] = useState<string[]>(() =>
    detail.phone_numbers.map(() => '')
  );
  const [closesAccount, setClosesAccount] = useState(false);
  const [clicked, setClicked] = useState(false);
  // A ref as well as state: a second click can land before the re-render.
  const clickedRef = useRef(false);

  const isFull = detail.port_type === 'full';
  const byInvoice = isFull && detail.invoice_port_offer === 'full';
  // With no numbers listed, every() is vacuously true; nothing to confirm.
  const allMatch =
    detail.phone_numbers.length > 0 &&
    detail.phone_numbers.every(
      (number, index) => toE164(typed[index] ?? '') === number
    );
  const ready = allMatch && (!isFull || closesAccount);

  const handleConfirm = async () => {
    if (clickedRef.current || !ready) return;
    clickedRef.current = true;
    setClicked(true);
    try {
      const { data } = await submitPortOrder({
        path: { port_order_id: detail.id },
        body: {
          confirm_phone_numbers: detail.phone_numbers,
          ...(isFull ? { confirm_full_port: true } : {}),
        },
        throwOnError: true,
      });
      showSuccess('Port submitted');
      onSubmitted(data);
    } catch (error) {
      logFailure('Failed to submit fax number port', error);
      showError(describeSubmitError(error));
      onFailed();
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && !clicked && onClose()}>
      <DialogContent className='max-h-[90vh] overflow-y-auto sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>Submit port</DialogTitle>
          <DialogDescription>
            Your current carrier is asked to move these numbers to Roger. This
            cannot be undone. Type each number to confirm.
          </DialogDescription>
        </DialogHeader>
        <div className='grid gap-4 py-2'>
          <dl className='grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-[auto_1fr]'>
            <dt className='text-muted-foreground'>Authorization</dt>
            <dd className='break-words'>
              {detail.documents.loa.filename ?? 'Signed letter'}
            </dd>
            <dt className='text-muted-foreground'>Invoice</dt>
            <dd className='break-words'>{invoiceSummary(detail)}</dd>
            {isFull && (
              <>
                <dt className='text-muted-foreground'>Account</dt>
                <dd>
                  Every number moves. Your carrier account closes.
                  {byInvoice && (
                    <span className='mt-1 block'>
                      Your invoice lists only these numbers:{' '}
                      {detail.invoice_account_phone_numbers
                        .map(displayPhone)
                        .join(', ')}
                      .
                    </span>
                  )}
                </dd>
              </>
            )}
          </dl>
          {detail.phone_numbers.length === 0 && (
            <p className='text-sm text-destructive'>
              This order does not list its numbers yet. Refresh it before
              submitting.
            </p>
          )}
          {detail.phone_numbers.map((number, index) => (
            <div key={number} className='grid gap-1.5'>
              <Label htmlFor={`port-confirm-${index}`}>
                Type {displayPhone(number)}
              </Label>
              <Input
                id={`port-confirm-${index}`}
                type='tel'
                autoComplete='off'
                value={typed[index] ?? ''}
                disabled={clicked}
                onChange={event => {
                  const value = event.target.value;
                  setTyped(current =>
                    current.map((entry, i) => (i === index ? value : entry))
                  );
                }}
              />
            </div>
          ))}
          {isFull && (
            <div className='flex items-start gap-2'>
              <Checkbox
                id='port-confirm-full'
                className='mt-0.5'
                checked={closesAccount}
                disabled={clicked}
                onCheckedChange={value => setClosesAccount(value === true)}
              />
              <Label
                htmlFor='port-confirm-full'
                className='font-normal leading-5'
              >
                {byInvoice
                  ? 'These are all the numbers on your carrier account. Every one moves, and the account closes when the port completes.'
                  : 'Every number on your carrier account moves, and the account closes when the port completes.'}
              </Label>
            </div>
          )}
          <p className='flex items-start gap-2 text-sm text-muted-foreground'>
            <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />
            Faxes to these numbers stop arriving at your current carrier on the
            port date.
          </p>
        </div>
        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={clicked}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            disabled={!ready || clicked}
          >
            {clicked && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SubmitDialog;
